import { scoreMatch, tokenizeQuery } from '@src/utils/search';
import { CheckState, CheckStateType } from '../designConstants';
import { MenuItem, SubmenuSelection } from './dropdown-constants';

export const isMultiMenuItems = <T>(
  menuItems: MenuItem<T>[] | MenuItem<T>[][]
): menuItems is MenuItem<T>[][] => Array.isArray(menuItems[0]);

export const hasSubmenuItems = <T>(items: readonly MenuItem<T>[]): boolean =>
  items.some(item => (item.children?.length ?? 0) > 0);

// Submenus are supported only on a single-column menu.
export const getSubmenuRootItems = <T>(
  menuItems: MenuItem<T>[] | MenuItem<T>[][]
): MenuItem<T>[] | null => {
  if (isMultiMenuItems(menuItems)) return null;
  return hasSubmenuItems(menuItems) ? menuItems : null;
};

// Child values only need to be unique within their parent, so child keys include the parent.
export const getSubmenuItemKey = <T>(item: MenuItem<T>): string =>
  item.parentValue == null ? String(item.value) : JSON.stringify([item.parentValue, item.value]);

export const getSubmenuCheckState = <T extends string>(
  item: MenuItem<T>,
  selection: SubmenuSelection<T>
): CheckStateType => {
  if (item.parentValue != null) {
    return Object.hasOwn(selection, item.parentValue) &&
      selection[item.parentValue]?.includes(item.value)
      ? CheckState.CHECKED
      : CheckState.UNCHECKED;
  }
  const selectedChildren = Object.hasOwn(selection, item.value) ? selection[item.value] : undefined;
  if (selectedChildren == null) return CheckState.UNCHECKED;
  return selectedChildren.length > 0 ? CheckState.INDETERMINATE : CheckState.CHECKED;
};

export const isSubmenuItemSelected = <T extends string>(
  item: MenuItem<T>,
  selection: SubmenuSelection<T>
): boolean => getSubmenuCheckState(item, selection) !== CheckState.UNCHECKED;

export const toggleSubmenuItem = <T extends string>(
  item: MenuItem<T>,
  selection: SubmenuSelection<T>
): SubmenuSelection<T> => {
  const next = { ...selection };
  const { parentValue, value } = item;

  if (parentValue == null) {
    if (Object.hasOwn(next, value)) {
      delete next[value];
    } else {
      next[value] = [];
    }
    return next;
  }

  const selectedChildren = (Object.hasOwn(next, parentValue) && next[parentValue]) || [];
  next[parentValue] = selectedChildren.includes(value)
    ? selectedChildren.filter(childValue => childValue !== value)
    : [...selectedChildren, value];
  return next;
};

// Parents come first, each followed by its children. Children inherit the parent's `section` so
// sectioned menus keep them grouped together.
export const flattenSubmenuItems = <T>(items: readonly MenuItem<T>[]): MenuItem<T>[] =>
  items.flatMap(parent => [
    parent,
    ...(parent.children ?? []).map(child => ({
      ...child,
      parentValue: parent.value,
      section: child.section ?? parent.section
    }))
  ]);

export const matchesLabelQuery = (
  { label, value }: { label?: string; value?: unknown },
  query: string
): boolean => {
  const tokens = tokenizeQuery(query);
  if (tokens.length === 0) return true;
  const text = label ?? (value != null ? String(value) : '');
  return scoreMatch(text, tokens) > 0;
};

// A parent that matches keeps all its children; a child that matches keeps its parent.
export const filterSubmenuItems = <T>(
  items: readonly MenuItem<T>[],
  predicate: (item: MenuItem<T>) => boolean
): MenuItem<T>[] =>
  items.flatMap(parent => {
    if (predicate(parent)) return [parent];
    const matchingChildren = (parent.children ?? []).filter(predicate);
    return matchingChildren.length > 0 ? [{ ...parent, children: matchingChildren }] : [];
  });

export const getSelectedSubmenuItems = <T extends string>(
  items: readonly MenuItem<T>[],
  selection: SubmenuSelection<T>
): MenuItem<T>[] =>
  flattenSubmenuItems(items).filter(item => isSubmenuItemSelected(item, selection));

export const getSubmenuSummary = <T extends string>(
  items: readonly MenuItem<T>[],
  selection: SubmenuSelection<T>
): string | undefined => {
  const selectedLabels = getSelectedSubmenuItems(items, selection).map(
    item => item.label ?? item.value
  );
  if (selectedLabels.length === 0) return undefined;
  return selectedLabels.length === 1
    ? selectedLabels[0]
    : `${selectedLabels.length} items selected`;
};
